import { LockClosedIcon } from '@heroicons/react/24/outline'
import { PlusCircleIcon, SparklesIcon } from '@heroicons/react/24/solid'
import { useFormik } from 'formik'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { useQuery } from '@tanstack/react-query'
import { QUERY_KEYS } from 'constants/constants'
import { ActivityIndicator } from '..'
import {
  useNotificationContext,
  usePartnerContext,
  usePaymentContext
} from '../../context'
import { useFeatureGate } from '../../features/pricing/use-feature-gate'
import { query } from '../../graphql'
import { EMAIL_REGEX, classNames } from '../../helpers'
import { usePartnerIntegrationCreate } from '../../hooks/use-partner-integration-create'
import { usePartnerIntegrationDelete } from '../../hooks/use-partner-integration-delete'
import { usePartnerIntegrations } from '../../hooks/use-partner-integrations'
import Divider from '../divider'
import Input from '../input'
import LockBadge from '../pricing/lock-badge'
import UpgradeInline from '../pricing/upgrade-inline'
import Button from '../tailwind/Button'
import InfoCallout from '../tailwind/InfoCallout'
import Modal from '../tailwind/Modal'
import Toggle from '../tailwind/toggle'

const TRANSLATION_LOCATION = 'settings.integrations.'

const INTEGRATION_FEATURE = 'PARTNER_ATS_INTEGRATION'
const INTEGRATION_SOURCE = 'settings_integrations'

/**
 * Was ein Klick auf eine Integrationskarte tut. In legacy und preview wie
 * bisher, ohne das Produkt öffnet jede Karte die Paywall (aktive Karten sind
 * dort ausgeblendet). In enforced bleiben aktive Integrationen öffenbar, damit
 * sie sich ansehen und entfernen lassen. Eine neue Integration öffnet den
 * Upgrade-Dialog.
 *
 * @returns {'open'|'paywall'|'dialog'}
 */
export const integrationCardAction = ({ enforced, available, isActive }) => {
  if (available) return 'open'
  if (!enforced) return 'paywall'
  return isActive ? 'open' : 'dialog'
}

// pdf_bundle (beide Reports als eine PDF ans ATS) gibt es nur fuer die
// Services, die das Backend in integrationsList dafuer freigibt.
const supportsPdfBundle = (integration, definitions) => {
  if (!integration || !definitions) return false
  const definition =
    definitions[integration.remote_service_name || integration.remote_service]
  return Boolean(
    definition?.integration_settings?.some?.(
      ({ field }) => field === 'pdf_bundle'
    )
  )
}

const Integrations = () => {
  const { t } = useTranslation()
  const { success, alert } = useNotificationContext()
  const { integrations: integrationsAvailable, showPaywall } =
    usePaymentContext()
  const { partner } = usePartnerContext()

  const [displayAddIntegration, setDisplayAddIntegration] = useState(false)
  const [displayActiveIntegration, setDisplayActiveIntegration] =
    useState(false)
  const [isEditMode, setIsEditMode] = useState(false)

  const [selectedIntegration, setSelectedIntegration] = useState(null)

  const { status: integrationsStatus, data: integrations } = useQuery({
    queryKey: QUERY_KEYS.integrations,
    queryFn: () =>
      query({
        query: 'integrationControl',
        variables: { action: 'listIntegrations' }
      }),
    select: (response) => {
      const integrationsResult = {}
      JSON.parse(response).listIntegrations.res.forEach((i) => {
        // Add our new field to each integration's settings
        const enhancedIntegration = {
          ...i,
          integration_settings: i.integration_settings.map((setting) => {
            return setting
          })
        }

        // Add information_mail_on_error field if it doesn't exist
        const hasErrorMailField = enhancedIntegration.integration_settings.some(
          (setting) => setting.field === 'information_mail_on_error'
        )

        if (!hasErrorMailField) {
          enhancedIntegration.integration_settings.push({
            field: 'information_mail_on_error',
            required: false
          })
        }

        integrationsResult[i.remote_service_name || i.remote_service] =
          enhancedIntegration
      })

      return integrationsResult
    }
  })

  const { status: partnerIntegrationsStatus, data: partnerIntegrations } =
    usePartnerIntegrations({})

  // Neue Lock-UX nur in enforced. Dort bleiben aktive Integrationen sichtbar,
  // Entfernen geht immer, Bearbeiten und neue Integrationen sind gesperrt. In
  // legacy und preview bleibt alles wie bisher.
  const gate = useFeatureGate(INTEGRATION_FEATURE, {
    source: INTEGRATION_SOURCE,
    inUse: !!partnerIntegrations?.length
  })
  const showActiveIntegrations = integrationsAvailable || gate.locked
  // Schlägt die Query nach ihren Wiederholungen fehl, gibt es keine Daten.
  // Seit aktive Integrationen in enforced auch ohne das Produkt sichtbar
  // bleiben, trifft das jeden gesperrten Kunden, deshalb die leere Liste.
  const activeIntegrations = partnerIntegrations || []

  const createIntegration = usePartnerIntegrationCreate()
  const deleteIntegration = usePartnerIntegrationDelete()

  const initialValues = useMemo(() => {
    if (!selectedIntegration) return {}

    // For active integrations in edit mode
    if (selectedIntegration.isActive && isEditMode) {
      const result = {}

      // Copy only existing values from the integration settings
      // For active integrations, integration_settings is an object with key-value pairs
      Object.keys(selectedIntegration.integration_settings || {}).forEach(
        (key) => {
          // Only include fields that are actually in the integration_settings
          // and have a value (not empty)
          if (selectedIntegration.integration_settings[key]) {
            result[key] = selectedIntegration.integration_settings[key]
          }
        }
      )

      // Make sure information_mail_on_error is included in the form
      if (
        !Object.prototype.hasOwnProperty.call(
          result,
          'information_mail_on_error'
        )
      ) {
        result.information_mail_on_error =
          selectedIntegration.integration_settings.information_mail_on_error ||
          ''
      }

      // Toggle: ein false ist oben rausgefiltert, ohne den Key gaebe es im
      // Formular keinen Schalter und die Option liesse sich nie einschalten
      if (supportsPdfBundle(selectedIntegration, integrations)) {
        result.pdf_bundle = Boolean(
          selectedIntegration.integration_settings?.pdf_bundle
        )
      }

      // Do not include any other fields that are not in the integration_settings
      // or don't have a value
      return result
    }

    // For new integrations
    if (!selectedIntegration.isActive) {
      const result = {}

      selectedIntegration.integration_settings.forEach(({ field }) => {
        result[field] = field === 'pdf_bundle' ? false : ''
      })

      // Make sure information_mail_on_error is included in the form
      if (
        !Object.prototype.hasOwnProperty.call(
          result,
          'information_mail_on_error'
        )
      ) {
        result.information_mail_on_error = ''
      }

      return result
    }

    return {}
  }, [selectedIntegration, isEditMode, integrations])

  const yup = useMemo(() => {
    return {
      email: () =>
        Yup.string().matches(
          EMAIL_REGEX,
          t(TRANSLATION_LOCATION + 'email_validation')
        ),
      assessment_status_reference: () => Yup.string(),
      assessment_test_reference: () => Yup.string(),
      password: () => Yup.string(),
      username: () => Yup.string(),
      url: () => Yup.string().url(t(TRANSLATION_LOCATION + 'url_validation')),
      dvinci_api_user: () => Yup.string(),
      dvinci_api_token: () => Yup.string(),
      token: () => Yup.string(),
      external_customer_id: () => Yup.string(),
      information_mail_on_error: () =>
        Yup.string().matches(
          EMAIL_REGEX,
          t(TRANSLATION_LOCATION + 'email_validation')
        ),
      pdf_bundle: () => Yup.boolean()
    }
  }, [t])

  const validationSchema = useMemo(() => {
    if (!selectedIntegration) return null

    // For active integrations in edit mode
    if (selectedIntegration.isActive && isEditMode) {
      const result = {}

      // Add validation only for fields that exist in the integration_settings
      // and have a value (not empty)
      Object.keys(selectedIntegration.integration_settings || {}).forEach(
        (key) => {
          // Only validate fields that have a value and a validation rule
          if (
            yup[key] &&
            selectedIntegration.integration_settings[key] &&
            selectedIntegration.integration_settings[key].length > 0
          ) {
            // If the field already has a value, we'll consider it required to prevent removing it
            result[key] = yup[key]().required(
              t(TRANSLATION_LOCATION + 'required_validation')
            )
          }
        }
      )

      // Always add validation for information_mail_on_error (not required)
      if (!result.information_mail_on_error && yup.information_mail_on_error) {
        result.information_mail_on_error = yup.information_mail_on_error()
      }

      // Only validate fields that are actually in the integration_settings with values
      // or information_mail_on_error
      return Yup.object(result)
    }

    // For new integrations
    if (!selectedIntegration.isActive) {
      const result = {}

      selectedIntegration.integration_settings.forEach(
        ({ field, required }) => {
          result[field] = required
            ? yup[field]().required(
                t(TRANSLATION_LOCATION + 'required_validation')
              )
            : yup[field]()
        }
      )

      return Yup.object(result)
    }

    return null
  }, [selectedIntegration, isEditMode, yup, t])

  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      // Common input properties for both create and update
      const baseInput = {
        authorized_create: ['__create_' + partner.id],
        authorized_read: ['__read_' + partner.id],
        authorized_update: ['__update_' + partner.id],
        authorized_delete: ['__delete_' + partner.id],
        partner_id: partner.id,
        integration_settings: values,
        remote_service: selectedIntegration.remote_service,
        remote_service_name: selectedIntegration.remote_service_name || null
      }

      // If we're in edit mode for an active integration
      if (selectedIntegration.isActive && isEditMode) {
        const input = {
          ...baseInput,
          id: selectedIntegration.id // Include the ID for update
        }

        // Since we're using the createIntegration hook for updates,
        // we need to delete the existing integration first and then create a new one with the same ID
        deleteIntegration.mutate(
          { input: { id: selectedIntegration.id } },
          {
            onSuccess: () => {
              // After deleting, create a new integration with the updated values
              createIntegration.mutate(
                { input },
                {
                  onSuccess: async () => {
                    success(t(TRANSLATION_LOCATION + 'update_success_message'))

                    setIsEditMode(false)
                    setDisplayActiveIntegration(false)
                    // await close modal transition
                    await new Promise((resolve) =>
                      setTimeout(resolve, 1000 * 0.4)
                    )
                    setSelectedIntegration(null)
                    formik.resetForm()
                    formik.setSubmitting(false)
                  },
                  onError: () => formik.setSubmitting(false)
                }
              )
            },
            onError: () => formik.setSubmitting(false)
          }
        )
      } else {
        // For new integrations
        createIntegration.mutate(
          { input: baseInput },
          {
            onSuccess: async () => {
              success(t(TRANSLATION_LOCATION + 'create_success_message'))

              setDisplayAddIntegration(false)
              // await close modal transition
              await new Promise((resolve) => setTimeout(resolve, 1000 * 0.4))
              setSelectedIntegration(null)
              formik.resetForm()
              formik.setSubmitting(false)
            },
            onError: () => formik.setSubmitting(false)
          }
        )
      }
    }
  })

  const renderSettingField = (key) => {
    if (key === 'pdf_bundle') {
      // Eigenes Panel, damit sich der Schalter von den Textfeldern absetzt
      // (gleiches Muster wie die Setting-Boxen im Dashboard).
      return (
        <div className='rounded-lg border border-gray-200 bg-gray-50 px-4 py-3'>
          <Toggle
            appearance='with-left-label-and-description'
            enabled={Boolean(formik.values.pdf_bundle)}
            setEnabled={(value) => formik.setFieldValue('pdf_bundle', value)}
            label={t(`${TRANSLATION_LOCATION}pdf_bundle_label`)}
            description={t(`${TRANSLATION_LOCATION}pdf_bundle_description`)}
          />
        </div>
      )
    }

    return (
      <Input
        id={key}
        type='text'
        label={t(`${TRANSLATION_LOCATION}${key}_label`)}
        placeholder={t(`${TRANSLATION_LOCATION}${key}_placeholder`)}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        touched={formik.touched[key]}
        value={formik.values[key] || ''}
        error={formik.errors[key]}
        autoComplete='off'
      />
    )
  }

  const deleteIntegrationHandler = ({ id }) => {
    alert(
      t(TRANSLATION_LOCATION + 'modal_remove_integration_title'),
      t(TRANSLATION_LOCATION + 'modal_remove_integration_description'),
      [
        {
          onPress: () => {
            deleteIntegration.mutate(
              { input: { id } },
              {
                onSuccess: () =>
                  success(t(TRANSLATION_LOCATION + 'remove_success_message'))
              }
            )
          },
          text: t(
            TRANSLATION_LOCATION + 'modal_remove_integration_submit_action'
          )
        },
        {
          text: t(
            TRANSLATION_LOCATION + 'modal_remove_integration_cancel_action'
          ),
          style: 'cancel'
        }
      ]
    )
  }

  if (
    integrationsStatus === 'pending' ||
    partnerIntegrationsStatus === 'pending'
  ) {
    return <ActivityIndicator className='flex justify-center py-16' />
  }

  const getIntegrationListItem = ({ integration, index }) => {
    const { logo_landscape, isActive } = integration

    const openIntegration = () => {
      if (isActive) {
        setDisplayActiveIntegration(true)
        setIsEditMode(false)
      } else {
        setDisplayAddIntegration(true)
      }

      setSelectedIntegration(integration)
    }

    const cardAction = integrationCardAction({
      enforced: gate.enforced,
      available: integrationsAvailable,
      isActive
    })

    return (
      <div
        key={index}
        onClick={
          cardAction === 'open'
            ? openIntegration
            : cardAction === 'dialog'
              ? gate.open
              : () => showPaywall('general')
        }
        className='group relative h-32 w-64 rounded-xl bg-gray-50 px-16 py-8 hover:cursor-pointer hover:bg-gray-100'
      >
        <div className='h-12'>
          <img
            alt=''
            src={logo_landscape}
            className='h-full w-full object-contain'
          />
        </div>

        {isActive ? (
          <>
            <div
              className={classNames(
                'flex items-center justify-center pr-3',
                'absolute left-2 top-2',
                'rounded-full bg-white group-hover:bg-gray-50'
              )}
            >
              <div className='h-6 w-6 rounded-full p-0.5'>
                <div
                  style={{ background: '#29CCC7' }} // primary green
                  className='h-full w-full rounded-full'
                />
              </div>
              <span className='ml-1 text-xs font-semibold text-gray-600 group-hover:text-gray-700'>
                {t(TRANSLATION_LOCATION + 'create_integration_active_label')}
              </span>
            </div>
            {/* Display Integration ID in the card */}
            <div className='absolute bottom-2 left-2 text-xs text-gray-500'>
              ID: {integration.id.substring(0, 8)}...
            </div>
          </>
        ) : gate.locked ? (
          <LockBadge
            feature={INTEGRATION_FEATURE}
            source={INTEGRATION_SOURCE}
            inUse={gate.readOnly}
            iconOnly
            className='absolute right-2 top-2'
          />
        ) : (
          <div
            className={classNames(
              'absolute flex items-center justify-center rounded-full',
              integrationsAvailable
                ? 'bottom-2 right-2 bg-white pl-3 group-hover:bg-gray-50'
                : '-right-1 -top-1 h-6 w-6 bg-amber-400'
            )}
          >
            {integrationsAvailable ? (
              <>
                <span className='mr-1 text-xs font-semibold text-gray-600 group-hover:text-gray-700'>
                  {t(
                    TRANSLATION_LOCATION + 'create_integration_activate_action'
                  )}
                </span>
                <PlusCircleIcon className='h-6 w-6 text-gray-600 group-hover:text-gray-700' />{' '}
              </>
            ) : (
              <SparklesIcon
                className='mx-auto my-auto h-4 w-4 text-white'
                aria-hidden='true'
              />
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <div>
        <h1 className='text-xl font-semibold text-gray-900'>
          {t(TRANSLATION_LOCATION + 'settings_container_title')}
        </h1>
        <p className='mt-1 max-w-lg text-sm text-gray-700 lg:max-w-2xl'>
          {t(TRANSLATION_LOCATION + 'settings_container_description')}
        </p>
      </div>
      <div className='mt-8 flex max-w-6xl flex-wrap gap-4'>
        {showActiveIntegrations &&
          activeIntegrations.map((integration, index) => {
            const { remote_service_name, remote_service } = integration
            const { logo, logo_landscape } =
              integrations[remote_service_name || remote_service]

            return getIntegrationListItem({
              integration: {
                ...integration,
                logo,
                logo_landscape,
                isActive: true
              },
              index
            })
          })}
        {showActiveIntegrations && !!activeIntegrations.length && (
          <div className='w-full px-8'>
            <Divider />
          </div>
        )}

        {Object.keys(integrations).map((key, index) =>
          getIntegrationListItem({
            integration: { id: key, ...integrations[key], isActive: false },
            index
          })
        )}
      </div>
      <Modal
        open={displayActiveIntegration}
        setOpen={() => {
          setDisplayActiveIntegration(false)
          setIsEditMode(false)
        }}
        size='3xl'
      >
        <div className='mt-3 text-center sm:mt-5'>
          <h2 className='text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl'>
            {t(TRANSLATION_LOCATION + 'active_integration_popup_title')}
          </h2>
        </div>
        <div className='mx-auto mb-8 mt-4 max-w-xl'>
          {selectedIntegration && selectedIntegration.isActive && (
            <>
              <div className='flex items-center justify-center'>
                <img
                  className='block h-8 w-8 object-contain'
                  src={selectedIntegration.logo}
                  alt='integration-logo'
                />
                <span className='text-md ml-4 block font-medium text-gray-900'>
                  {t(
                    TRANSLATION_LOCATION +
                      `${
                        selectedIntegration.remote_service_name ||
                        selectedIntegration.remote_service
                      }_title`
                  )}
                </span>
              </div>
              <div className='mt-4'>
                {!isEditMode ? (
                  // View mode
                  <div className='px-2 py-4'>
                    {/* Display Integration ID */}
                    <div className='mt-4 flex'>
                      <span className='block text-sm text-gray-700'>
                        {t(`${TRANSLATION_LOCATION}integration_id_label`)}:
                      </span>
                      <span className='ml-2 block text-sm font-medium text-gray-900'>
                        {selectedIntegration.id}
                      </span>
                    </div>

                    {Object.keys(selectedIntegration.integration_settings || {})
                      .filter(
                        (key) =>
                          // Filter out information_mail_on_error as it's displayed separately
                          key !== 'information_mail_on_error' &&
                          key !== 'pdf_bundle' &&
                          selectedIntegration.integration_settings[key]
                      )
                      .map((key, index) => {
                        const setting = {
                          key,
                          value: selectedIntegration.integration_settings[key]
                        }

                        return (
                          <div key={index} className='mt-4 flex'>
                            <span className='block text-sm text-gray-700'>
                              {t(`${TRANSLATION_LOCATION}${key}_label`)}:
                            </span>
                            <span className='ml-2 block text-sm font-medium text-gray-900'>
                              {setting.value}
                            </span>
                          </div>
                        )
                      })}

                    {/* Show information_mail_on_error field if it exists */}
                    {selectedIntegration.integration_settings
                      .information_mail_on_error && (
                      <div className='mt-4 flex'>
                        <span className='block text-sm text-gray-700'>
                          {t(
                            `${TRANSLATION_LOCATION}information_mail_on_error_label`
                          )}
                          :
                        </span>
                        <span className='ml-2 block text-sm font-medium text-gray-900'>
                          {
                            selectedIntegration.integration_settings
                              .information_mail_on_error
                          }
                        </span>
                      </div>
                    )}

                    {supportsPdfBundle(selectedIntegration, integrations) && (
                      <div className='mt-4 flex'>
                        <span className='block text-sm text-gray-700'>
                          {t(`${TRANSLATION_LOCATION}pdf_bundle_label`)}:
                        </span>
                        <span className='ml-2 block text-sm font-medium text-gray-900'>
                          {selectedIntegration.integration_settings.pdf_bundle
                            ? t(`${TRANSLATION_LOCATION}pdf_bundle_on`)
                            : t(`${TRANSLATION_LOCATION}pdf_bundle_off`)}
                        </span>
                      </div>
                    )}

                    {/* Always show information_mail_on_error field label even if empty */}
                    {!selectedIntegration.integration_settings
                      .information_mail_on_error && (
                      <div className='mt-4 flex'>
                        <span className='block text-sm text-gray-700'>
                          {t(
                            `${TRANSLATION_LOCATION}information_mail_on_error_label`
                          )}
                          :
                        </span>
                        <span className='ml-2 block text-sm italic text-gray-500'>
                          {t(`${TRANSLATION_LOCATION}no_error_email_set`)}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  // Edit mode
                  <div className='mt-8'>
                    {/* Display Integration ID (read-only) */}
                    <div className='mt-4 flex'>
                      <span className='block text-sm text-gray-700'>
                        {t(`${TRANSLATION_LOCATION}integration_id_label`)}:
                      </span>
                      <span className='ml-2 block text-sm font-medium text-gray-900'>
                        {selectedIntegration.id}
                      </span>
                    </div>

                    {/* Show editable fields - only those that exist in the integration_settings */}
                    {Object.keys(formik.initialValues)
                      .filter((key) => {
                        // Only show fields that are already in the integration_settings
                        // or the information_mail_on_error field which should always be editable
                        return (
                          key === 'information_mail_on_error' ||
                          key === 'pdf_bundle' ||
                          Object.keys(
                            selectedIntegration.integration_settings || {}
                          ).includes(key)
                        )
                      })
                      .map((key, index) => (
                        <div key={index} className='mt-4'>
                          {renderSettingField(key)}
                        </div>
                      ))}
                  </div>
                )}

                {!isEditMode && gate.locked && (
                  <div className='mt-4 space-y-4 px-2'>
                    <InfoCallout tone='neutral' icon={LockClosedIcon}>
                      {t('pricing.gates.integration_edit_hint')}
                    </InfoCallout>
                    <UpgradeInline
                      feature={INTEGRATION_FEATURE}
                      source={INTEGRATION_SOURCE}
                      compact
                    />
                  </div>
                )}

                <div className='mb-4 mt-8 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3'>
                  {!isEditMode ? (
                    // View mode buttons
                    <>
                      <Button.SecondaryLG
                        text={t(
                          TRANSLATION_LOCATION +
                            'active_integration_remove_action'
                        )}
                        onClick={() => {
                          setDisplayActiveIntegration(false)
                          deleteIntegrationHandler({
                            id: selectedIntegration.id
                          })
                        }}
                      />
                      {/* Gesperrt in enforced bleiben nur Entfernen und
                          Schließen, Bearbeiten entfällt */}
                      <div
                        className={
                          gate.locked
                            ? 'grid grid-cols-1 gap-2'
                            : 'grid grid-cols-2 gap-2'
                        }
                      >
                        {!gate.locked && (
                          <Button.SecondaryLG
                            text={t(
                              TRANSLATION_LOCATION +
                                'active_integration_edit_action'
                            )}
                            onClick={() => setIsEditMode(true)}
                          />
                        )}
                        <Button.PrimaryLG
                          text={t(
                            TRANSLATION_LOCATION +
                              'active_integration_close_action'
                          )}
                          onClick={() => setDisplayActiveIntegration(false)}
                        />
                      </div>
                    </>
                  ) : (
                    // Edit mode buttons
                    <>
                      <Button.SecondaryLG
                        text={t(
                          TRANSLATION_LOCATION +
                            'active_integration_cancel_edit_action'
                        )}
                        onClick={() => {
                          setIsEditMode(false)
                          formik.resetForm()
                        }}
                      />
                      <Button.PrimaryLG
                        text={t(
                          TRANSLATION_LOCATION +
                            'active_integration_save_action'
                        )}
                        onClick={formik.handleSubmit}
                        isLoading={formik.isSubmitting}
                        disabled={!formik.isValid}
                      />
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
      <Modal
        open={displayAddIntegration}
        setOpen={() => setDisplayAddIntegration(false)}
        size='3xl'
      >
        <div className='mt-3 text-center sm:mt-5'>
          <h2 className='text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl'>
            {t(TRANSLATION_LOCATION + 'create_integration_modal_title')}
          </h2>
        </div>
        <div className='mx-auto mb-8 mt-4 max-w-xl'>
          {selectedIntegration && !selectedIntegration.isActive && (
            <>
              <div className='flex items-center justify-center'>
                <img
                  className='block h-8 w-8 object-contain'
                  src={selectedIntegration.logo}
                  alt='integration-logo'
                />
                <span className='text-md ml-4 block font-medium text-gray-900'>
                  {t(
                    TRANSLATION_LOCATION +
                      `${
                        selectedIntegration.remote_service_name ||
                        selectedIntegration.remote_service
                      }_title`
                  )}
                </span>
              </div>
              <div className='mt-8'>
                {Object.keys(formik.initialValues).map((key, index) => (
                  <div key={index} className='mt-4'>
                    {renderSettingField(key)}
                  </div>
                ))}
              </div>
              <div className='mb-4 mt-8 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3'>
                <Button.SecondaryLG
                  text={t(
                    TRANSLATION_LOCATION + 'create_integration_cancel_action'
                  )}
                  onClick={() => setDisplayAddIntegration(false)}
                />
                <Button.PrimaryLG
                  text={t(
                    TRANSLATION_LOCATION + 'create_integration_create_action'
                  )}
                  onClick={formik.handleSubmit}
                  isLoading={formik.isSubmitting}
                  disabled={!formik.isValid}
                />
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  )
}

export default Integrations
